import * as React from "react";

/**
 * Sentinel-driven infinite scroll over a fixed in-memory array, mock-paginated
 * to mirror a real backend cursor. Shared by the ej-health listing grids
 * (ListingPage → medical-news / topic, and the doctors-lounge video grid).
 * Returns the current page slice, whether more remain, and the sentinel ref to
 * attach below the grid.
 */
export function useInfiniteList<T>(
  items: T[],
  pageSize: number,
  initialCount: number = pageSize,
) {
  const [visibleCount, setVisibleCount] = React.useState(initialCount);
  const [loading, setLoading] = React.useState(false);
  const sentinelRef = React.useRef<HTMLDivElement | null>(null);

  const hasMore = visibleCount < items.length;
  const visible = items.slice(0, visibleCount);

  React.useEffect(() => {
    if (!hasMore) return;
    const node = sentinelRef.current;
    if (!node) return;

    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting) && !loading) {
          setLoading(true);
          // Mock async fetch — in production this would await the API.
          window.setTimeout(() => {
            setVisibleCount((c) => Math.min(items.length, c + pageSize));
            setLoading(false);
          }, 300);
        }
      },
      { rootMargin: "300px 0px" },
    );
    io.observe(node);
    return () => io.disconnect();
  }, [hasMore, loading, items.length, pageSize]);

  return { visible, hasMore, sentinelRef };
}
